import { UzButton, UzTabs } from '@utazzo/ui';
import { FileSpreadsheet, Plus } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ActivitiesMediaManager } from '../components/ActivitiesMediaManager';

import { ActivitiesListPage, ViewToggle, type View } from './ActivitiesListPage';

import { PageShell } from '@/components/page-shell';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

type Tab = 'listing' | 'media';
const isTab = (v: string | null): v is Tab => v === 'listing' || v === 'media';

/**
 * Activities inbox - the home of the activities module.
 *
 * Wraps two tabs (mirrors the Transfers inbox):
 *   - **Listing** - the full DataGrid of activities, re-uses `ActivitiesListPage`
 *     in embedded mode so the page header (title + actions) is owned here.
 *   - **Media** - bulk cover/gallery image assignment across activities.
 *
 * Tab state is URL-driven (`?tab=listing|media`) so operators can share a
 * direct link.
 */
export const ActivitiesInboxPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();
  const raw = params.get('tab');
  const tab: Tab = isTab(raw) ? raw : 'listing';
  // View state lives here so the ViewToggle can sit in the action bar next
  // to Bulk import + Add (mirrors the hotels list layout). Passed down as a
  // controlled prop to `ActivitiesListPage`.
  const [view, setView] = useState<View>('list');

  // Merges into the LIVE URL - the listing tab's grid state (rows-per-page,
  // filters, sort) is written by nuqs, which React Router's params snapshot
  // never sees. Rebuilding from that snapshot would drop it on every switch.
  const setTab = (next: Tab): void => {
    writeParams((p) => p.set('tab', next));
  };

  return (
    <PageShell
      title="Activities"
      description="In-house activities catalog. Transfers, visas and meals are separate modules."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {tab === 'listing' && <ViewToggle view={view} onChange={setView} />}
          <UzButton variant="outline" size="sm" onClick={() => navigate('/activities/imports')}>
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            Bulk import
          </UzButton>
          <UzButton size="sm" onClick={() => navigate('/activities/new')}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add activity
          </UzButton>
        </div>
      }
    >
      <UzTabs
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        tabs={[
          {
            value: 'listing',
            label: 'Listing',
            content: <ActivitiesListPage embedded view={view} onViewChange={setView} />,
          },
          {
            value: 'media',
            label: 'Media',
            content: <ActivitiesMediaManager />,
          },
        ]}
      />
    </PageShell>
  );
};
